import React from 'react';
import { StoryProvider } from './StoryContext';
import { CharacterProvider } from './CharacterContext';
import { ReadAloudProvider } from './ReadAloudContext';
import { AuthProvider } from './AuthContext';
import { useLocation } from 'react-router-dom';
import ReadAloudControlBar from '../components/ReadAloudControlBar';

/**
 * AppProviders is a component that combines all context providers
 * in a single component. This allows for easier management of
 * provider dependencies and ensures correct nesting order.
 * 
 * The order of providers is important:
 * 1. AuthProvider first as other providers may depend on authentication state
 * 2. CharacterProvider second, as the Story might need character data
 * 3. StoryProvider next
 * 4. ReadAloudProvider last as it depends on story data
 * 
 * The component is designed to prevent unnecessary re-mounting
 * of context providers when navigating between routes.
 */
// NOTE:
// Wrapping the application routes with the various context providers **inside the component
// render itself** (instead of inside a `useMemo` that depends on `children`) ensures that the
// provider components stay *mounted* for the entire lifetime of the application.  React will
// keep the same provider instances alive across route changes, only updating their `children`
// prop.  This prevents situations such as the ReadAloudProvider being torn down and recreated
// every time the user navigates between pages, which in turn was interrupting the speech
// synthesis service.
export function AppProviders({ children }) {
  return (
    <AuthProvider>
      <CharacterProvider>
        <StoryProvider>
          <ReadAloudProvider>
            {children}
            {/* Persistent UI element that controls the read‑aloud feature */}
            <ReadAloudControlBar />
          </ReadAloudProvider>
        </StoryProvider>
      </CharacterProvider>
    </AuthProvider>
  );
}

export default AppProviders;